import React from "react";
import {
  TrendingUp,
  BarChart3,
  PieChart,
  Shield,
  DollarSign,
  Coins,
  Triangle,
  Circle,
  Square,
} from "lucide-react";
import { useLoadingStates } from "@/store";

export const FinancialLoadingScreen: React.FC = () => {
  const {
    isLoading,
    isCompanyUserLoading,
    isCompanyLoading,
    isPermissionsLoading,
  } = useLoadingStates();

  // Define loading steps in logical order
  const loadingSteps = [
    {
      key: "session",
      loading: isLoading,
      icon: Shield,
      text: "Securing your session...",
    },
    {
      key: "companyUser",
      loading: isCompanyUserLoading,
      icon: TrendingUp,
      text: "Loading your account...",
    },
    {
      key: "company",
      loading: isCompanyLoading,
      icon: BarChart3,
      text: "Fetching company data...",
    },
    {
      key: "permissions",
      loading: isPermissionsLoading,
      icon: PieChart,
      text: "Setting up permissions...",
    },
  ];

  // Calculate progress based on loading sequence
  // If any step is still loading, show progress based on which step we're on
  // If no steps are loading, we're complete (100%)
  const anyLoading = loadingSteps.some((step) => step.loading);

  let progress = 0;
  let currentStepIndex = 0;

  if (!anyLoading) {
    // All steps completed
    progress = 100;
    currentStepIndex = loadingSteps.length - 1;
  } else {
    // Find the first step that's still loading
    currentStepIndex = loadingSteps.findIndex((step) => step.loading);
    if (currentStepIndex === -1) currentStepIndex = 0;

    // Progress is based on which step we're currently on
    progress = (currentStepIndex / loadingSteps.length) * 100;
  }

  const currentStep = loadingSteps[currentStepIndex];
  const CurrentIcon = currentStep.icon;
  const completedSteps = anyLoading ? currentStepIndex : loadingSteps.length;
  const totalSteps = loadingSteps.length;

  // Falling elements configuration - mix of pre-populated and falling from above
  const fallingElements = [
    // Pre-populated on screen (start immediately)
    { Icon: DollarSign, left: "10%", top: "10%", duration: "8s", delay: "0s" },
    { Icon: Coins, left: "20%", top: "25%", duration: "10s", delay: "0s" },
    { Icon: Triangle, left: "30%", top: "40%", duration: "9s", delay: "0s" },
    { Icon: Circle, left: "40%", top: "15%", duration: "11s", delay: "0s" },
    { Icon: Square, left: "60%", top: "30%", duration: "9s", delay: "0s" },
    { Icon: Coins, left: "70%", top: "45%", duration: "12s", delay: "0s" },
    { Icon: Triangle, left: "80%", top: "20%", duration: "8s", delay: "0s" },
    { Icon: Circle, left: "15%", top: "70%", duration: "9s", delay: "0s" },
    { Icon: Square, left: "35%", top: "80%", duration: "11s", delay: "0s" },
    { Icon: DollarSign, left: "85%", top: "35%", duration: "12s", delay: "0s" },

    // Falling from above screen (start immediately)
    { Icon: DollarSign, left: "5%", top: "-60px", duration: "7s", delay: "0s" },
    { Icon: Coins, left: "15%", top: "-40px", duration: "9s", delay: "0s" },
    { Icon: Triangle, left: "25%", top: "-80px", duration: "8s", delay: "0s" },
    { Icon: Circle, left: "35%", top: "-30px", duration: "10s", delay: "0s" },
    { Icon: Square, left: "45%", top: "-70px", duration: "11s", delay: "0s" },
    {
      Icon: DollarSign,
      left: "55%",
      top: "-50px",
      duration: "7s",
      delay: "0s",
    },
    { Icon: Coins, left: "65%", top: "-90px", duration: "12s", delay: "0s" },
    { Icon: Triangle, left: "75%", top: "-35px", duration: "9s", delay: "0s" },
    { Icon: Circle, left: "85%", top: "-65px", duration: "8s", delay: "0s" },
    { Icon: Square, left: "95%", top: "-45px", duration: "10s", delay: "0s" },

    // More falling from above with slight offsets
    {
      Icon: DollarSign,
      left: "12%",
      top: "-100px",
      duration: "8s",
      delay: "0s",
    },
    { Icon: Coins, left: "28%", top: "-120px", duration: "9s", delay: "0s" },
    { Icon: Triangle, left: "42%", top: "-85px", duration: "11s", delay: "0s" },
    { Icon: Circle, left: "58%", top: "-110px", duration: "7s", delay: "0s" },
    { Icon: Square, left: "72%", top: "-95px", duration: "10s", delay: "0s" },
    {
      Icon: DollarSign,
      left: "88%",
      top: "-75px",
      duration: "12s",
      delay: "0s",
    },
  ];

  return (
    <div className="fixed inset-0 bg-background flex items-center justify-center overflow-hidden">
      {/* Falling elements */}
      <div className="absolute inset-0 pointer-events-none">
        {fallingElements.map((element, index) => {
          const { Icon, left, top, duration, delay } = element;
          return (
            <div
              key={index}
              className="absolute opacity-20 falling-element"
              style={{
                left,
                top,
                animation: `fallFromPosition ${duration} linear ${delay} infinite`,
              }}
            >
              <Icon size={24} className="text-ink-disabled" />
            </div>
          );
        })}
      </div>

      {/* Simple background pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-2 h-2 bg-line-mid rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-32 w-1 h-1 bg-line-dashed rounded-full animate-pulse delay-1000"></div>
        <div className="absolute bottom-32 left-1/3 w-1.5 h-1.5 bg-line-mid rounded-full animate-pulse delay-2000"></div>
        <div className="absolute bottom-20 right-20 w-2 h-2 bg-line-dashed rounded-full animate-pulse delay-500"></div>
      </div>

      {/* Main content */}
      <div className="text-center z-10">
        {/* Icon with simple rotation */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <CurrentIcon
              size={64}
              className="text-ink-muted"
              style={{ animationDuration: "3s" }}
            />
            <div className="absolute inset-0 rounded-full border-2 border-line-dashed/30 animate-ping"></div>
          </div>
        </div>

        {/* Real-time loading text */}
        <h2 className="text-2xl font-semibold text-ink mb-4 animate-pulse">
          {currentStep.text}
        </h2>

        {/* Real progress bar */}
        <div className="w-64 h-1 bg-line-mid rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Progress percentage */}
        <div className="text-sm text-ink-muted mb-6">
          {Math.round(progress)}% Complete ({completedSteps}/{totalSteps})
        </div>

        {/* Real loading state dots */}
        <div className="flex justify-center space-x-2">
          {loadingSteps.map((step, index) => (
            <div
              key={step.key}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                index < currentStepIndex ||
                (!anyLoading && index === currentStepIndex)
                  ? "bg-primary"
                  : index === currentStepIndex
                  ? "bg-ink-faint animate-pulse"
                  : "bg-line-mid"
              }`}
              title={step.text}
            />
          ))}
        </div>
      </div>

      {/* CSS Animation for falling elements */}
      <style>{`
        @keyframes fallFromPosition {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.2;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        /* Accessibility: Respect prefers-reduced-motion */
        @media (prefers-reduced-motion: reduce) {
          /* Disable falling animations */
          .falling-element {
            animation: none !important;
          }
          
          /* Disable spinning icon */
          .spinning-icon {
            animation: none !important;
          }
          
          /* Disable pulsing effects */
          .animate-pulse {
            animation: none !important;
          }
          
          /* Disable ping effect */
          .animate-ping {
            animation: none !important;
          }
          
          /* Keep essential transitions but make them instant */
          .transition-colors {
            transition-duration: 0s !important;
          }
          
          .transition-all {
            transition-duration: 0s !important;
          }
        }
      `}</style>
    </div>
  );
};
